import type { FC } from 'react';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';

import { FormField, FormInput, Label } from '@/shared/ui';

import type { OfferFormValues } from '../libs/offerSchema';

export interface OfferFeesSectionProps {
  control: Control<OfferFormValues>;
  currency: string;
  setValue: UseFormSetValue<OfferFormValues>;
  values: OfferFormValues;
}

export const OfferFeesSection: FC<OfferFeesSectionProps> = ({
  control,
  currency,
  setValue,
  values,
}) => {
  const { t } = useTranslation();
  const suffix = currency ? ` (${currency})` : '';

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField
          control={control}
          name="guarantee_amount"
          render={({ field }) => (
            <div className="space-y-2">
              <Label>
                {t('pages.admin.offers.fields.guarantee')}
                {suffix}
              </Label>
              <FormInput {...field} inputMode="decimal" />
            </div>
          )}
        />
        <FormField
          control={control}
          name="refund"
          render={({ field }) => (
            <div className="space-y-2">
              <Label>
                {t('pages.admin.offers.fields.refund')}
                {suffix}
              </Label>
              <FormInput {...field} inputMode="decimal" />
            </div>
          )}
        />
        <FormField
          control={control}
          name="chargeback"
          render={({ field }) => (
            <div className="space-y-2">
              <Label>
                {t('pages.admin.offers.fields.chargeback')}
                {suffix}
              </Label>
              <FormInput {...field} inputMode="decimal" />
            </div>
          )}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <FormField
          control={control}
          name="rolling_reserve.reserve_pct"
          render={({ field }) => (
            <div className="space-y-2">
              <Label>{t('pages.admin.offers.fields.reservePct')}</Label>
              <FormInput {...field} inputMode="decimal" />
            </div>
          )}
        />
        <FormField
          control={control}
          name="rolling_reserve.hold_days"
          render={({ field }) => (
            <div className="space-y-2">
              <Label>{t('pages.admin.offers.fields.holdDays')}</Label>
              <FormInput {...field} inputMode="numeric" />
            </div>
          )}
        />
        <FormField
          control={control}
          name="rolling_reserve.cap_amount"
          render={({ field }) => (
            <div className="space-y-2">
              <Label>
                {t('pages.admin.offers.fields.capAmount')}
                {suffix}
              </Label>
              <FormInput {...field} inputMode="decimal" />
            </div>
          )}
        />
      </div>
      <FormField
        control={control}
        name="requirements"
        render={({ field }) => (
          <div className="space-y-1.5">
            <Label>{t('pages.admin.offers.fields.requirements')}</Label>
            <FormInput
              {...field}
              placeholder={t(
                'pages.admin.offers.fields.requirementsPlaceholder',
              )}
            />
          </div>
        )}
      />
      <FormField
        control={control}
        name="flow"
        render={({ field }) => (
          <div className="space-y-1.5">
            <Label>{t('pages.admin.offers.fields.flow')}</Label>
            <FormInput
              {...field}
              placeholder={t('pages.admin.offers.fields.flowPlaceholder')}
            />
          </div>
        )}
      />
      <FormField
        control={control}
        name="details"
        render={({ field }) => (
          <div className="space-y-1.5">
            <Label>{t('pages.admin.offers.fields.details')}</Label>
            <textarea
              {...field}
              className="min-h-20 w-full resize-none rounded-xl border border-border/60 bg-background/60 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
              placeholder={t('pages.admin.offers.fields.detailsPlaceholder')}
              rows={3}
            />
          </div>
        )}
      />
      <div className="space-y-2 border-t border-border/40 pt-3">
        <p className="text-[11px] font-medium text-muted-foreground">
          {t('pages.admin.offers.fields.customFields')}
        </p>
        <p className="text-[11px] text-muted-foreground">
          {t('pages.admin.offers.fields.customFieldsHint')}
        </p>
        {values.custom_fields.map((field, index) => (
          <div
            key={`${field.key}-${index}`}
            className="flex items-center gap-2">
            <input
              className="w-full rounded-xl border border-border/60 bg-background/60 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
              placeholder={t('pages.admin.offers.fields.customFieldName')}
              value={field.key}
              onChange={(event) => {
                const next = [...values.custom_fields];

                next[index] = { ...field, key: event.target.value };
                setValue('custom_fields', next);
              }}
            />
            <input
              className="w-full rounded-xl border border-border/60 bg-background/60 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
              placeholder={t('pages.admin.offers.fields.customFieldValue')}
              value={field.value}
              onChange={(event) => {
                const next = [...values.custom_fields];

                next[index] = { ...field, value: event.target.value };
                setValue('custom_fields', next);
              }}
            />
            <button
              aria-label={t('buttons.delete')}
              className="shrink-0 cursor-pointer rounded-lg p-2 text-muted-foreground hover:bg-rose-500/10 hover:text-rose-500"
              type="button"
              onClick={() =>
                setValue(
                  'custom_fields',
                  values.custom_fields.filter(
                    (_, itemIndex) => itemIndex !== index,
                  ),
                )
              }>
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        <button
          className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-300"
          type="button"
          onClick={() =>
            setValue('custom_fields', [
              ...values.custom_fields,
              { key: '', value: '' },
            ])
          }>
          <Plus className="h-3.5 w-3.5" />
          {t('pages.admin.offers.fields.addCustomField')}
        </button>
      </div>
    </div>
  );
};

OfferFeesSection.displayName = 'OfferFeesSection';
